// OperationReport45Component.jsx
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Label, Table } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseISO } from "date-fns";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import { GetVMdDept } from "../../share/api/MasterDataApi";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

/* ===== locales ===== */
registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

const thaiMonths = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const years = range(1963, new Date().getFullYear() + 8);

/* ===== helpers ===== */
const groupBy = (arr, key) =>
  (arr || []).reduce((a, it) => {
    const k = (it?.[key] ?? "").toString();
    (a[k] ??= []).push(it);
    return a;
  }, {});
const isUniform = (rows, field) =>
  rows.every(r => (r[field] ?? "") === (rows[0]?.[field] ?? ""));

const N = v => Number(String(v ?? 0).toString().replace(/,/g, ""));
function fmtMoney(n) { const num = Number(String(n ?? 0).replace(/,/g, "")); return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }); }
function toThaiDateLabel(v) {
  if (!v) return "-";
  if (typeof v === "string" && /\s[ก-๙]/.test(v)) return v; // already BE string
  const d = typeof v === "string" ? parseISO(v) : v;
  if (!(d instanceof Date) || isNaN(d)) return "-";
  const be = addYears(543, d);
  const monthName = format(be, "MMMM", { locale: th });
  return `${format(be, "d")} ${monthName} ${format(be, "yyyy")}`;
}
function formatMonthYearBE(date) { if (!date) return ""; const be = addYears(543, date); return format(be, "MMMM yyyy", { locale: th }); }

/* ===== PDF Thai font (optional) ===== */
function base64FromArrayBuffer(buf) { const b = new Uint8Array(buf); const c = 0x8000; let s = ""; for (let i = 0; i < b.length; i += c) { s += String.fromCharCode.apply(null, b.subarray(i, i + c)); } return btoa(s); }
let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;
  const [regRes, boldRes] = await Promise.all([fetch("/fonts/Sarabun-Regular.ttf"), fetch("/fonts/Sarabun-Bold.ttf")]);
  const [regBuf, boldBuf] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
  const regB64 = base64FromArrayBuffer(regBuf); const boldB64 = base64FromArrayBuffer(boldBuf);
  jsPDF.API.events.push(['addFonts', function () {
    this.addFileToVFS("Sarabun-Regular.ttf", regB64);
    this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
    this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
    this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
  }]);
  new jsPDF(); thaiFontLoaded = true;
}

/* ===== Component ===== */
export function OperationReport45Component() {
  const [reportMonth, setReportMonth] = useState("");
  const [reportMonthDate, setReportMonthDate] = useState(null);

  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const roleId = String(user?.roleId ?? user?.roldId ?? "");
  const userDeptId = String(user?.deptId ?? "all");

  const [deptsData, setDeptsData] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [deptIdFilter, setDeptIdFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  // เงื่อนไขโชว์คอลัมน์หน่วยงาน
  const showDeptCol = (String(user?.roldId ?? roleId) === "4" && String(deptIdFilter || "all") === "all");

  useEffect(() => { GetVMdDept({ setDeptsData, setLoading }); }, []);
  useEffect(() => {
    const mapped = (deptsData || []).map(d => ({
      value: String(d?.value ?? d?.deptId ?? d?.DeptId ?? d?.id ?? ""),
      label: String(d?.label ?? d?.deptName ?? d?.DeptName ?? d?.name ?? "")
    }));
    if (roleId === "4") { setDeptOptions([{ value: "all", label: "ทุกหน่วยงาน" }, ...mapped]); setDeptIdFilter(p => p ?? "all"); }
    else if (roleId === "3") { const self = mapped.find(o => o.value === userDeptId) || { value: userDeptId, label: "หน่วยงานของฉัน" }; setDeptOptions([self]); setDeptIdFilter(userDeptId); }
    else { setDeptOptions(mapped); setDeptIdFilter(p => p || (mapped.find(o => o.value === userDeptId)?.value ?? "")); }
  }, [deptsData, roleId, userDeptId]);

  useEffect(() => {
    if (!reportMonth) { setReportMonthDate(null); return; }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  /* ====== โหลดข้อมูลจาก API (mapping ตาม service ที่ให้มา) ====== */
  const checkAndLoadMonth = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("monthYear", reportMonth === "" ? "all" : reportMonth);
      fd.append("deptIdFilter", deptIdFilter || "all");

      const res = await fetch("/api/SupportOperation/listSupportOperation45", {
        method: "POST",
        headers: { token: user.token },
        body: fd
      });
      const j = await res.json();
      const raw = Array.isArray(j?.dataenum) ? j.dataenum : [];

      const pick = (obj, ...ks) => ks.map(k => obj?.[k]).find(v => v !== undefined && v !== null && String(v).trim() !== "") ?? "";

      const mapped = raw.map(x => ({
        SupportNo: pick(x, "SupportNo", "supportNo"),
        SupportName: pick(x, "SupportName", "supportName"),
        DeptName: pick(x, "deptName", "DeptName", "approveDeptName", "ApproveDeptName"), // <<<<<< เพิ่ม
        InnovationName: pick(x, "InnovationName", "innovationName"),
        InnovationCourseName: pick(x, "InnovationCourseName", "innovationCourseName"),
        UpdateDate: pick(x, "UpdateDate", "updateDate"),
        Hour: N(pick(x, "Hour", "hour")),
        ClassAmount: N(pick(x, "ClassAmount", "classAmount")),
        StartDate: pick(x, "StartDate", "startDate"),
        EndDate: pick(x, "EndDate", "endDate"),
        PersonTotal: N(pick(x, "PersonTotal", "personTotal")),
        TutorHourAmount: N(pick(x, "TutorHourAmount", "tutorHourAmount")),
        MaterialAmount: N(pick(x, "MaterialAmount", "materialAmount")),
        PersonAmount: N(pick(x, "PersonAmount", "personAmount")),
        SendTutorAmount: 0,
        SendMaterialAmount: 0,
        SendRegisterAmount: 0,
        Amount: N(pick(x, "Amount", "amount")),
        PaymentAmount: N(String(pick(x, "PaymentAmount", "paymentAmount")).replace(/,/g, "")),
        PaymentDate: pick(x, "PaymentDate", "paymentDate"),
      }));

      setItems(mapped);
    } catch (e) {
      console.error(e);
      Swal.fire("โหลดข้อมูลไม่สำเร็จ", "", "error");
      setItems([]);
    } finally { setLoading(false); }
  };

  const totalPayment = items.reduce((s, r) => s + N(r.PaymentAmount), 0);
  const totalAmount = items.reduce((s, r) => s + N(r.Amount), 0);

  /* ===== Header 3 แถว (สำหรับ PDF) — dynamic DeptName ===== */
  const headRow1 = [
    { content: "ที่", rowSpan: 3 },
    { content: "เลขที่คำขอ", rowSpan: 3 },
    { content: "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน", rowSpan: 3 },
    ...(showDeptCol ? [{ content: "หน่วยงานที่อนุมัติคำขอ", rowSpan: 3 }] : []),
    { content: "ชื่อหลักสูตรสร้างสรรค์นวัตกรรม", rowSpan: 3 },
    { content: "ชื่อหลักสูตรเทคโนโลยี", rowSpan: 3 },
    { content: "หลักสูตรได้รับความเห็นชอบ", rowSpan: 3 },
    { content: "ระยะเวลาฝึกอบรม", rowSpan: 3 },
    { content: "จำนวน", rowSpan: 3 },
    { content: "วันที่เริ่มฝึก", rowSpan: 3 },
    { content: "วันที่จบฝึก", rowSpan: 3 },
    { content: "จำนวนลูกจ้างที่เข้ารับการฝึกอบรม", rowSpan: 3 },
    { content: "ค่าใช้จ่ายในการฝึกอบรม", colSpan: 6 },
    { content: "รวมจำนวน", rowSpan: 3 },
    { content: "จำนวนเงินช่วยเหลือหรืออุดหนุน", rowSpan: 3 },
    { content: "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน", rowSpan: 3 },
  ];
  const headRow2 = [
    "", "", ...(showDeptCol ? [""] : []), "", "", "", "", "", "", "", "",
    { content: "ฝึกอบรมเอง", colSpan: 3 },
    { content: "ส่งไปฝึก", colSpan: 3 },
    "", "", "",
  ];
  const headRow3 = [
    "", "", ...(showDeptCol ? [""] : []), "", "", "(ว/ด/ป)", "(ชม.)", "(รุ่น)", "", "", "(คน)",
    "ค่าวิทยากร (บาท)", "ค่าวัสดุ (บาท)", "ค่าลงทะเบียน (บาท)",
    "ค่าวิทยากร (บาท)", "ค่าวัสดุ (บาท)", "ค่าลงทะเบียน (บาท)",
    "(บาท)", "(บาท)", "",
  ];

  /* ===== Excel AoA + merges (dynamic DeptName) ===== */
  const buildAoaAndMerges = () => {
    const row1 = [
      "ที่", "เลขที่คำขอ", "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน",
      ...(showDeptCol ? ["หน่วยงานที่อนุมัติคำขอ"] : []),
      "ชื่อหลักสูตรสร้างสรรค์นวัตกรรม", "ชื่อหลักสูตรเทคโนโลยี", "หลักสูตรได้รับความเห็นชอบ", "ระยะเวลาฝึกอบรม", "จำนวน",
      "วันที่เริ่มฝึก", "วันที่จบฝึก", "จำนวนลูกจ้างที่เข้ารับการฝึกอบรม",
      "ค่าใช้จ่ายในการฝึกอบรม", "", "", "", "", "",
      "รวมจำนวน", "จำนวนเงินช่วยเหลือหรืออุดหนุน", "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน",
    ];
    const row2 = [
      "", "", ...(showDeptCol ? [""] : []), "", "", "", "", "", "", "", "",
      "ฝึกอบรมเอง", "", "", "ส่งไปฝึก", "", "",
      "", "", "",
    ];
    const row3 = [
      "", "", ...(showDeptCol ? [""] : []), "", "", "(ว/ด/ป)", "(ชม.)", "(รุ่น)", "", "", "(คน)",
      "ค่าวิทยากร (บาท)", "ค่าวัสดุ (บาท)", "ค่าลงทะเบียน (บาท)",
      "ค่าวิทยากร (บาท)", "ค่าวัสดุ (บาท)", "ค่าลงทะเบียน (บาท)",
      "(บาท)", "(บาท)", "",
    ];

    const aoa = [row1, row2, row3];
    const merges = [];

    // คอลัมน์ต้น (rowSpan 3)
    const fixed = showDeptCol ? 4 : 3;
    for (let c = 0; c < fixed; c++) merges.push({ s: { r: 0, c }, e: { r: 2, c } });

    // กลุ่มหัวข้อถัดไป
    const certStart = fixed; // เริ่มที่ "ชื่อหลักสูตรสร้างสรรค์นวัตกรรม"
    // ไม่มี colSpan เพิ่มสำหรับสองคอลัมน์แรก (ชื่อหลักสูตร x2) แต่ทั้งคู่ rowSpan 3
    merges.push({ s: { r: 0, c: certStart + 0 }, e: { r: 2, c: certStart + 0 } }); // ชื่อหลักสูตรสร้างสรรค์นวัตกรรม
    merges.push({ s: { r: 0, c: certStart + 1 }, e: { r: 2, c: certStart + 1 } }); // ชื่อหลักสูตรเทคโนโลยี
    merges.push({ s: { r: 0, c: certStart + 2 }, e: { r: 2, c: certStart + 2 } }); // หลักสูตรได้รับความเห็นชอบ
    merges.push({ s: { r: 0, c: certStart + 3 }, e: { r: 2, c: certStart + 3 } }); // ระยะเวลาฝึกอบรม
    merges.push({ s: { r: 0, c: certStart + 4 }, e: { r: 2, c: certStart + 4 } }); // จำนวน
    merges.push({ s: { r: 0, c: certStart + 5 }, e: { r: 2, c: certStart + 5 } }); // วันที่เริ่มฝึก
    merges.push({ s: { r: 0, c: certStart + 6 }, e: { r: 2, c: certStart + 6 } }); // วันที่จบฝึก
    merges.push({ s: { r: 0, c: certStart + 7 }, e: { r: 2, c: certStart + 7 } }); // จำนวนลูกจ้าง...

    // กลุ่มค่าใช้จ่าย 6 คอลัมน์
    const costStart = certStart + 8;
    merges.push({ s: { r: 0, c: costStart }, e: { r: 0, c: costStart + 5 } });
    merges.push({ s: { r: 1, c: costStart + 0 }, e: { r: 1, c: costStart + 2 } }); // ฝึกอบรมเอง
    merges.push({ s: { r: 1, c: costStart + 3 }, e: { r: 1, c: costStart + 5 } }); // ส่งไปฝึก

    // รวม/ช่วยเหลือ/วันที่จ่าย rowSpan 3
    const sumCol = costStart + 6;
    const payCol = costStart + 7;
    const dateCol = costStart + 8;
    merges.push({ s: { r: 0, c: sumCol }, e: { r: 2, c: sumCol } });
    merges.push({ s: { r: 0, c: payCol }, e: { r: 2, c: payCol } });
    merges.push({ s: { r: 0, c: dateCol }, e: { r: 2, c: dateCol } });

    // เติมข้อมูล
    let cur = 3;
    Object.entries(groupBy(items, "SupportNo")).forEach(([sn, rows], gIdx) => {
      const span = rows.length;
      const uAmt = isUniform(rows, "Amount");
      const uPay = isUniform(rows, "PaymentAmount");
      const uPayDt = isUniform(rows, "PaymentDate");

      rows.forEach((r, idx) => {
        const row = [];
        row[0] = idx ? "" : gIdx + 1;
        row[1] = idx ? "" : (r.SupportNo || "-");
        row[2] = idx ? "" : (r.SupportName || "-");
        if (showDeptCol) row[3] = idx ? "" : (r.DeptName || "-");

        const base = showDeptCol ? 4 : 3;
        row[base + 0] = r.InnovationName || "-";
        row[base + 1] = r.InnovationCourseName || "-";
        row[base + 2] = toThaiDateLabel(r.UpdateDate);
        row[base + 3] = r.Hour;
        row[base + 4] = r.ClassAmount;
        row[base + 5] = toThaiDateLabel(r.StartDate);
        row[base + 6] = toThaiDateLabel(r.EndDate);
        row[base + 7] = r.PersonTotal;

        row[base + 8] = r.TutorHourAmount;
        row[base + 9] = r.MaterialAmount;
        row[base + 10] = r.PersonAmount;

        row[base + 11] = r.SendTutorAmount;
        row[base + 12] = r.SendMaterialAmount;
        row[base + 13] = r.SendRegisterAmount;

        row[base + 14] = uAmt ? (idx ? "" : r.Amount) : r.Amount;
        row[base + 15] = uPay ? (idx ? "" : r.PaymentAmount) : r.PaymentAmount;
        row[base + 16] = uPayDt ? (idx ? "" : toThaiDateLabel(r.PaymentDate)) : toThaiDateLabel(r.PaymentDate);

        aoa.push(row);
        cur++;
      });

      // merge แนวตั้ง: SupportNo, SupportName, (DeptName)
      merges.push({ s: { r: cur - span, c: 1 }, e: { r: cur - 1, c: 1 } });
      merges.push({ s: { r: cur - span, c: 2 }, e: { r: cur - 1, c: 2 } });
      if (showDeptCol) merges.push({ s: { r: cur - span, c: 3 }, e: { r: cur - 1, c: 3 } });

      // merge แนวตั้งสำหรับ Amount/Payment/Date ถ้า uniform
      const base = showDeptCol ? 4 : 3;
      if (uAmt) merges.push({ s: { r: cur - span, c: base + 14 }, e: { r: cur - 1, c: base + 14 } });
      if (uPay) merges.push({ s: { r: cur - span, c: base + 15 }, e: { r: cur - 1, c: base + 15 } });
      if (uPayDt) merges.push({ s: { r: cur - span, c: base + 16 }, e: { r: cur - 1, c: base + 16 } });
    });

    return { aoa, merges };
  };

  const handleExportExcel = () => {
    if (!items.length) { Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning"); return; }
    const { aoa, merges } = buildAoaAndMerges();
    const wb = XLSX.utils.book_new(); const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;

    // widths (ปรับเมื่อมี DeptName)
    ws["!cols"] = showDeptCol
      ? [
          { wch: 4 }, { wch: 18 }, { wch: 36 }, { wch: 28 }, // + DeptName
          { wch: 28 }, { wch: 18 }, { wch: 10 }, { wch: 8 },
          { wch: 16 }, { wch: 16 }, { wch: 16 },
          { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 },
          { wch: 16 }, { wch: 18 }, { wch: 22 }
        ]
      : [
          { wch: 4 }, { wch: 18 }, { wch: 36 },
          { wch: 28 }, { wch: 18 }, { wch: 10 }, { wch: 8 },
          { wch: 16 }, { wch: 16 }, { wch: 16 },
          { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 },
          { wch: 16 }, { wch: 18 }, { wch: 22 }
        ];

    // footer sums
    const r = aoa.length;
    ws[XLSX.utils.encode_cell({ r, c: 0 })] = { t: "s", v: "รวม" };
    // คอลัมน์รวม/ช่วยเหลือ ขยับเมื่อ showDeptCol
    const base = showDeptCol ? 4 : 3;
    ws[XLSX.utils.encode_cell({ r, c: base + 14 })] = { t: "n", v: totalAmount };
    ws[XLSX.utils.encode_cell({ r, c: base + 15 })] = { t: "n", v: totalPayment };
    ws["!merges"].push({ s: { r, c: 0 }, e: { r, c: base + 13 } });

    XLSX.utils.book_append_sheet(wb, ws, "รายงาน ชอ.4-5");
    XLSX.writeFile(wb, `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-5.xlsx`);
  };

  const handleExportPDF = async () => {
    if (!items.length) { Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning"); return; }
    await ensureThaiFontLoaded();
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    doc.setFont("Sarabun", "bold"); doc.setFontSize(16);
    doc.text(`แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ.4-5`, 40, 40);

    const head = [
      headRow1.map(h => (typeof h === "string" ? { content: h } : { content: h.content, colSpan: h.colSpan, rowSpan: h.rowSpan, styles: { halign: "center", valign: "middle" } })),
      headRow2.map(h => (typeof h === "string" ? { content: h } : { content: h.content, colSpan: h.colSpan, rowSpan: h.rowSpan, styles: { halign: "center", valign: "middle" } })),
      headRow3.map(t => ({ content: t }))
    ];

    const body = [];
    Object.entries(groupBy(items, "SupportNo")).forEach(([sn, rows], gIdx) => {
      const span = rows.length;
      const uAmt = isUniform(rows, "Amount");
      const uPay = isUniform(rows, "PaymentAmount");
      const uPayDt = isUniform(rows, "PaymentDate");
      rows.forEach((r, idx) => {
        const row = [];
        row.push(
          idx ? "" : gIdx + 1,
          idx ? "" : (r.SupportNo || "-"),
          idx ? "" : (r.SupportName || "-"),
        );
        if (showDeptCol) row.push(idx ? "" : (r.DeptName || "-"));

        row.push(
          r.InnovationName || "-",
          r.InnovationCourseName || "-",
          toThaiDateLabel(r.UpdateDate),
          fmtMoney(r.Hour),
          fmtMoney(r.ClassAmount),
          toThaiDateLabel(r.StartDate),
          toThaiDateLabel(r.EndDate),
          fmtMoney(r.PersonTotal),
          fmtMoney(r.TutorHourAmount),
          fmtMoney(r.MaterialAmount),
          fmtMoney(r.PersonAmount),
          fmtMoney(r.SendTutorAmount),
          fmtMoney(r.SendMaterialAmount),
          fmtMoney(r.SendRegisterAmount),
          fmtMoney(uAmt ? (idx ? "" : r.Amount) : r.Amount),
          fmtMoney(uPay ? (idx ? "" : r.PaymentAmount) : r.PaymentAmount),
          uPayDt ? (idx ? "" : toThaiDateLabel(r.PaymentDate)) : toThaiDateLabel(r.PaymentDate),
        );
        body.push(row);
      });
    });

    doc.setFont("Sarabun", "normal");
    doc.autoTable({
      head, body, startY: 60,
      styles: { font: "Sarabun", fontSize: 10, cellPadding: 4, halign: "center", overflow: "linebreak" },
      headStyles: { fillColor: [255, 236, 224], textColor: [0, 0, 0], font: "Sarabun", fontStyle: "bold" },
      columnStyles: {
        2: { halign: "left" },
        ...(showDeptCol ? { 3: { halign: "left" }, 4: { halign: "left" } } : { 3: { halign: "left" } }),
        // ส่วนตัวเลขให้ชิดขวา
      },
      didDrawPage: (data) => {
        const n = doc.internal.getNumberOfPages();
        doc.setFont("Sarabun", "normal"); doc.setFontSize(9);
        doc.text(`หน้า ${data.pageNumber} / ${n}`, data.settings.margin.left, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.setFont("Sarabun", "bold"); doc.setFontSize(12);
    const y = (doc.lastAutoTable?.finalY || 60) + 24;
    doc.text(`รวมจำนวน (บาท): ${fmtMoney(totalAmount)}    |    รวมจำนวนเงินช่วยเหลือ: ${fmtMoney(totalPayment)} บาท`, 40, y);
    doc.save(`แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-5.pdf`);
  };

  /* ===== render ===== */
  return (
    <Container className="py-3">
      <Row className="g-3 align-items-end">
        <Col md={6}>
          <Label>หน่วยงานกรมพัฒนาฝีมือแรงงาน</Label>
          <Select
            options={deptOptions}
            value={deptOptions.find(o => o.value === (deptIdFilter || "all")) || null}
            onChange={opt => setDeptIdFilter(opt?.value ?? "all")}
            isDisabled={roleId === "3"}
            className="text-center input-color"
            styles={{ control: (b) => ({ ...b, borderRadius: "20px" }), singleValue: (b) => ({ ...b, color: "#0A66C2" }) }}
            placeholder="เลือกหน่วยงาน"
            noOptionsMessage={() => "ไม่พบหน่วยงาน"}
          />
        </Col>
        <Col md={6}>
          <Label>รอบรายงาน (เดือน/ปี)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear(); const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`); setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else { setReportMonth(""); setReportMonthDate(null); }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select className="form-select form-select-sm" style={{ width: 120 }} value={thaiMonths[getMonth(date)]} onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}>
                    {thaiMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 100 }} value={getYear(date)} onChange={({ target: { value } }) => changeYear(Number(value))}>
                    {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
              </div>
            )}
            customInput={<input className="form-control rounded-pill text-center input-color" readOnly value={formatMonthYearBE(reportMonthDate)} placeholder="เลือกเดือน/ปี (พ.ศ.)" />}
          />
        </Col>

        <Col md="auto" className="pt-4 d-flex justify-content-end gap-2">
          <button className="btn btn-primary" onClick={checkAndLoadMonth}>ตรวจสอบ/โหลดรายงาน</button>
          <button className="btn btn-outline-success" onClick={handleExportExcel}>ส่งออก Excel</button>
          <button className="btn btn-outline-danger" onClick={handleExportPDF}>ส่งออก PDF</button>
        </Col>
      </Row>

      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan={3} className="text-center align-middle">ที่</th>
              <th rowSpan={3} className="text-center align-middle">เลขที่คำขอ</th>
              <th rowSpan={3} className="text-center align-middle">ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน</th>
              {showDeptCol && (
                <th rowSpan={3} className="text-center align-middle">หน่วยงานที่อนุมัติคำขอ</th>
              )}
              <th rowSpan={3} className="text-center align-middle">ชื่อหลักสูตรสร้างสรรค์นวัตกรรม</th>
              <th rowSpan={3} className="text-center align-middle">ชื่อหลักสูตรเทคโนโลยี</th>
              <th rowSpan={3} className="text-center align-middle">หลักสูตรได้รับความเห็นชอบ<br />(ว/ด/ป)</th>
              <th rowSpan={3} className="text-center align-middle">ระยะเวลาฝึกอบรม<br />(ชม.)</th>
              <th rowSpan={3} className="text-center align-middle">จำนวน<br />(รุ่น)</th>
              <th rowSpan={3} className="text-center align-middle">วันที่เริ่มฝึก</th>
              <th rowSpan={3} className="text-center align-middle">วันที่จบฝึก</th>
              <th rowSpan={3} className="text-center align-middle">จำนวนลูกจ้างที่เข้ารับการฝึกอบรม<br />(คน)</th>
              <th colSpan={6} className="text-center align-middle">ค่าใช้จ่ายในการฝึกอบรม</th>
              <th rowSpan={3} className="text-center align-middle">รวมจำนวน<br />(บาท)</th>
              <th rowSpan={3} className="text-center align-middle">จำนวนเงินช่วยเหลือหรืออุดหนุน<br />(บาท)</th>
              <th rowSpan={3} className="text-center align-middle">วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
            </tr>
            <tr>
              <th colSpan={3} className="text-center align-middle">ฝึกอบรมเอง</th>
              <th colSpan={3} className="text-center align-middle">ส่งไปฝึก</th>
            </tr>
            <tr>
              <th className="text-center">ค่าวิทยากร<br />(บาท)</th>
              <th className="text-center">ค่าวัสดุ<br />(บาท)</th>
              <th className="text-center">ค่าลงทะเบียน<br />(บาท)</th>
              <th className="text-center">ค่าวิทยากร<br />(บาท)</th>
              <th className="text-center">ค่าวัสดุ<br />(บาท)</th>
              <th className="text-center">ค่าลงทะเบียน<br />(บาท)</th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={showDeptCol ? 21 : 20} className="text-center">ยังไม่มีรายการ</td></tr>
            )}

            {Object.entries(groupBy(items, "SupportNo")).map(([sn, rows], gIdx) => {
              const span = rows.length;
              const uAmt = isUniform(rows, "Amount");
              const uPay = isUniform(rows, "PaymentAmount");
              const uPayDt = isUniform(rows, "PaymentDate");

              return rows.map((r, idx) => (
                <tr key={`${sn}-${idx}`}>
                  <td className="text-center">{idx === 0 ? gIdx + 1 : ""}</td>
                  {idx === 0 && <td className="text-center" rowSpan={span}>{r.SupportNo || "-"}</td>}
                  {idx === 0 && <td className="text-start" rowSpan={span}>{r.SupportName || "-"}</td>}
                  {showDeptCol && idx === 0 && <td className="text-start" rowSpan={span}>{r.DeptName || "-"}</td>}

                  <td className="text-start">{r.InnovationName || "-"}</td>
                  <td className="text-start">{r.InnovationCourseName || "-"}</td>
                  <td className="text-center">{toThaiDateLabel(r.UpdateDate)}</td>
                  <td className="text-end">{fmtMoney(r.Hour)}</td>
                  <td className="text-end">{fmtMoney(r.ClassAmount)}</td>
                  <td className="text-center">{toThaiDateLabel(r.StartDate)}</td>
                  <td className="text-center">{toThaiDateLabel(r.EndDate)}</td>
                  <td className="text-end">{fmtMoney(r.PersonTotal)}</td>

                  {/* ฝึกอบรมเอง */}
                  <td className="text-end">{fmtMoney(r.TutorHourAmount)}</td>
                  <td className="text-end">{fmtMoney(r.MaterialAmount)}</td>
                  <td className="text-end">{fmtMoney(r.PersonAmount)}</td>

                  {/* ส่งไปฝึก (ไม่มีข้อมูล → 0) */}
                  <td className="text-end">{fmtMoney(r.SendTutorAmount)}</td>
                  <td className="text-end">{fmtMoney(r.SendMaterialAmount)}</td>
                  <td className="text-end">{fmtMoney(r.SendRegisterAmount)}</td>

                  {/* รวมจำนวน (บาท) */}
                  {uAmt
                    ? (idx === 0 ? <td className="text-end" rowSpan={span}>{fmtMoney(rows[0].Amount)}</td> : null)
                    : <td className="text-end">{fmtMoney(r.Amount)}</td>}

                  {/* จำนวนเงินช่วยเหลือ และ วันที่จ่าย */}
                  {uPay
                    ? (idx === 0 ? <td className="text-end" rowSpan={span}>{fmtMoney(rows[0].PaymentAmount)}</td> : null)
                    : <td className="text-end">{fmtMoney(r.PaymentAmount)}</td>}
                  {uPayDt
                    ? (idx === 0 ? <td className="text-center" rowSpan={span}>{toThaiDateLabel(rows[0].PaymentDate)}</td> : null)
                    : <td className="text-center">{toThaiDateLabel(r.PaymentDate)}</td>}
                </tr>
              ));
            })}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={showDeptCol ? 18 : 17} className="text-end fw-bold">รวม</td>
                <td className="text-end fw-bold">{fmtMoney(totalAmount)}</td>
                <td className="text-end fw-bold">{fmtMoney(totalPayment)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <div className="badge bg-secondary">กำลังโหลด...</div>
        </div>
      )}
    </Container>
  );
}
